import React, { useEffect, useState } from "react";
import SiteChrome from "@/components/SiteChrome";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { usePlatformMessages } from "@/lib/usePlatformMessages";

const POST_INSCRIPTION_LABELS = {
  pt: {
    ask_invoice: "Pedir fatura",
    pay_later_sent: "Enviámos um email com o link de pagamento.",
    processing: "A processar pagamento…",
    success: "Pagamento confirmado! Vais receber um email. A redireccionar…",
    choose_method: "Escolhe como pagar",
    submitted_title: "Inscrição enviada!",
    submitted_msg: "Verifique a sua caixa de correio.",
    back_home: "Voltar ao início",
  },
  en: {
    ask_invoice: "Request invoice",
    pay_later_sent: "We sent you an email with the payment link.",
    processing: "Processing payment…",
    success: "Payment confirmed! Check your email. Redirecting…",
    choose_method: "Choose how to pay",
    submitted_title: "Enrolment sent!",
    submitted_msg: "Please check your inbox.",
    back_home: "Back to home",
  },
  es: {
    ask_invoice: "Solicitar factura",
    pay_later_sent: "Te hemos enviado un email con el enlace de pago.",
    processing: "Procesando pago…",
    success: "¡Pago confirmado! Revisa tu email. Redirigiendo…",
    choose_method: "Elige cómo pagar",
    submitted_title: "¡Inscripción enviada!",
    submitted_msg: "Consulta tu bandeja de entrada.",
    back_home: "Volver al inicio",
  },
};

const LEVELS = [1, 2, 3, 4, 5];

export default function InscriptionForm() {
  const { t, lang } = useI18n();
  const { m } = usePlatformMessages();
  const [games, setGames] = useState([]);
  const [gameId, setGameId] = useState("");
  const [email, setEmail] = useState("");
  const [school, setSchool] = useState("");
  const [quantities, setQuantities] = useState({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const postLabels = POST_INSCRIPTION_LABELS[lang] || POST_INSCRIPTION_LABELS.pt;

  useEffect(() => {
    api.get(`/games?active_only=true&status=competition&lang=${encodeURIComponent(lang || "pt")}`).then((r) => {
      setGames(r.data);
      if (r.data.length) setGameId(r.data[0].id);
    });
  }, [lang]);

  // Per-game rows: tiers when game uses level_system="tiers"; otherwise 1..5.
  const currentGame = games.find((g) => g.id === gameId);
  const useTiers = currentGame?.level_system === "tiers" && Array.isArray(currentGame?.tiers) && currentGame.tiers.length > 0;
  const rows = useTiers
    ? currentGame.tiers.map((tr) => ({ key: tr.id, level: tr.level, label: tr.name, tier_id: tr.id }))
    : LEVELS.map((lv) => ({ key: lv, level: lv, label: `${t("level")} ${lv}`, tier_id: null }));
  const total = rows.reduce((acc, r) => acc + (quantities[r.key] || 0), 0);

  const submit = async () => {
    if (total <= 0) {
      toast.error(m("inscription_min_student", null, "Indica pelo menos 1 aluno num dos níveis"));
      return;
    }
    setLoading(true);
    try {
      const levels = rows
        .filter((r) => (quantities[r.key] || 0) > 0)
        .map((r) => ({ level: r.level, quantity: quantities[r.key], ...(r.tier_id ? { tier_id: r.tier_id } : {}) }));
      const { data } = await api.post(`/inscriptions?lang=${encodeURIComponent(lang || "pt")}`, {
        game_id: gameId,
        email,
        school,
        levels,
      });
      setResult(data);
      toast.success(m("inscription_success", { n: total }, `Inscrição registada! ${total} credenciais geradas.`));
      setQuantities({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || m("generic_error", null, "Erro"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteChrome>
      <div className="max-w-2xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-[Fredoka] font-semibold text-white mb-2">{t("school_register")}</h1>
        <p className="text-white/80 mb-8">{useTiers ? t("insc_generate_hint_tiers") : t("insc_generate_hint_levels")}</p>
        <div className="bg-white border border-slate-200 rounded-3xl p-8 space-y-4 shadow-sm">
          <div>
            <Label>{t("competition")}</Label>
            <Select value={gameId} onValueChange={setGameId}>
              <SelectTrigger data-testid="insc-game"><SelectValue /></SelectTrigger>
              <SelectContent>
                {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("email")}</Label>
              <Input data-testid="insc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label>{t("school")}</Label>
              <Input data-testid="insc-school" value={school} onChange={(e) => setSchool(e.target.value)} />
            </div>
          </div>

          <div>
            <Label>{useTiers ? t("insc_students_per_tier") : t("insc_students_per_level")}</Label>
            <div className="bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden mt-2">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-2 font-bold text-slate-700 text-xs uppercase">{useTiers ? t("insc_col_tier_or_level") : t("insc_col_level")}</th>
                    <th className="text-right px-4 py-2 font-bold text-slate-700 text-xs uppercase">{t("students_count")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-4 py-2 font-[Fredoka] font-bold text-slate-800">{r.label}{useTiers ? <span className="text-slate-400 text-xs font-normal ml-1">({t("insc_lvl_short")} {r.level})</span> : null}</td>
                      <td className="px-4 py-2 text-right">
                        <Input
                          data-testid={`insc-qty-${r.key}`}
                          type="number"
                          min={0}
                          value={quantities[r.key] || 0}
                          onChange={(e) => setQuantities({ ...quantities, [r.key]: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                          className="w-24 ml-auto text-right"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100">
                    <td className="px-4 py-2 font-bold text-slate-900">{t("insc_total")}</td>
                    <td className="px-4 py-2 text-right font-bold text-slate-900" data-testid="insc-total">{total}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <Button
            data-testid="btn-insc-submit"
            disabled={!email || !school || total <= 0 || loading}
            onClick={submit}
            className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-full font-bold py-6"
          >
            {t("submit")}
          </Button>
        </div>

        {result && (
          <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-3xl p-6 md:p-8 text-center" data-testid="insc-result">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500 text-white grid place-items-center text-3xl mb-4">✓</div>
            <h3 className="font-bold mb-1 text-emerald-900 font-[Fredoka] text-2xl">
              {postLabels.submitted_title}
            </h3>
            <p className="text-sm text-emerald-800 mb-6">
              {postLabels.submitted_msg}
            </p>
            <Button
              onClick={() => { window.location.href = "/"; }}
              data-testid="btn-insc-back-home"
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 px-6"
            >
              {postLabels.back_home}
            </Button>
          </div>
        )}
      </div>
    </SiteChrome>
  );
}
